if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        let formData = new FormData(this.form);

        const activatedAddOns = this.getActivatedAddOns();
        if (activatedAddOns.length > 0) {
          // Convert to items[] format for multiple products
          const items = [];

          // Add main product
          const mainVariantId = formData.get('id');
          const mainProductQuantity = parseInt(formData.get('quantity') || 1, 10);
          items.push({
            id: mainVariantId,
            quantity: mainProductQuantity
          });

          // Add checked add-ons (quantity limited by inventory if applicable)
          activatedAddOns.forEach((addOn) => {
            let addOnQuantity = mainProductQuantity;

            // Check inventory limits if inventory is managed by Shopify
            if (addOn.inventoryManagement === 'shopify' && addOn.inventoryPolicy !== 'continue') {
              const availableInventory = parseInt(addOn.inventoryQuantity, 10) || 0;
              // Limit quantity to available inventory
              addOnQuantity = Math.min(mainProductQuantity, availableInventory);
            }

            if (addOnQuantity > 0) {
              items.push({
                id: addOn.id,
                quantity: addOnQuantity
              });
            }
          });

          // Create new FormData with items[] format
          const newFormData = new FormData();
          items.forEach((item, index) => {
            newFormData.append(`items[${index}][id]`, item.id);
            newFormData.append(`items[${index}][quantity]`, item.quantity);
          });

          // Copy other form fields (like gift card recipient)
          for (const [key, value] of formData.entries()) {
            if (key !== 'id' && key !== 'quantity') {
              newFormData.append(key, value);
            }
          }

          // Replace formData with newFormData
          formData = newFormData;
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getActivatedAddOns() {
        const activatedAddOns = [];
        const addOnCheckboxes = document.querySelectorAll('.product-add-on--item-checkbox:checked');

        addOnCheckboxes.forEach((checkbox) => {
          const variantId = checkbox.getAttribute('data-add-on-variant-id');
          if (variantId) {
            activatedAddOns.push({
              id: variantId,
              inventoryQuantity: checkbox.getAttribute('data-add-on-inventory-quantity'),
              inventoryManagement: checkbox.getAttribute('data-add-on-inventory-management'),
              inventoryPolicy: checkbox.getAttribute('data-add-on-inventory-policy')
            });
          }
        });

        return activatedAddOns;
      }
    }
  );
}
